import { recordBootEvent } from '../../../bootstrap/boot-trace';
import { readFeatureEntitlementVault } from '../../local-authority';
import {
  type ExpectedFeatureEntitlementSubject,
  type FeatureEntitlementVerification,
  hasKnownFeaturePlanConflict,
  type SignedFeatureEntitlement,
  verifyFeatureEntitlement,
} from './feature-entitlement';

const FEATURE_CACHE_VERSION = 'v3-signed-entitlements';
const MAX_PRELOADED_SUBJECTS = 8;
export const LOCAL_FEATURE_ENTITLEMENT_PLAN = 'Free';

interface PreloadedFeatureEntitlement {
  readonly identity: string;
  readonly promise: Promise<FeatureEntitlementVerification>;
}

const preloadedEntitlements = new Map<string, PreloadedFeatureEntitlement>();

export function workspaceFeatureEntitlementCacheKey(
  workspaceId: string,
  userId: string
): string {
  return `sl-features-${FEATURE_CACHE_VERSION}-${workspaceId}-${userId}`;
}

export function planFeatureEntitlementCacheKey(plan: string): string {
  return `sl-features-${FEATURE_CACHE_VERSION}-plan-${plan.toLowerCase()}`;
}

function entitlementIdentity(
  expected: ExpectedFeatureEntitlementSubject
): string {
  return expected.subjectType === 'workspace'
    ? `workspace:${expected.workspaceId}:${expected.userId}`
    : `plan:${expected.plan.toLowerCase()}`;
}

function emptyVerification(
  status: 'missing' | 'expired' | 'invalid'
): FeatureEntitlementVerification {
  return {
    status,
    features: [],
    plan: null,
    expiresAt: null,
  };
}

async function readAndVerifyFeatureEntitlement(
  cacheKey: string,
  expected: ExpectedFeatureEntitlementSubject
): Promise<FeatureEntitlementVerification> {
  const readStartedAt = performance.now();
  const cached =
    await readFeatureEntitlementVault<SignedFeatureEntitlement>(cacheKey);
  recordBootEvent('feature-entitlement-cache-read', {
    durationMs: performance.now() - readStartedAt,
    found: cached.status === 'ready' && Boolean(cached.value),
  });
  if (cached.status !== 'ready' || !cached.value) {
    return emptyVerification('missing');
  }
  const verifyStartedAt = performance.now();
  const result = await verifyFeatureEntitlement(cached.value, expected);
  recordBootEvent('feature-entitlement-cache-verified', {
    durationMs: performance.now() - verifyStartedAt,
    status: result.status,
  });
  return result;
}

function evictOldestPreload(): void {
  const oldestKey = preloadedEntitlements.keys().next().value as
    | string
    | undefined;
  if (oldestKey) {
    preloadedEntitlements.delete(oldestKey);
  }
}

/**
 * Starts only the local vault read and signature verification. It never applies
 * features, changes body classes, or starts network revalidation.
 */
export function preloadFeatureEntitlement(
  cacheKey: string,
  expected: ExpectedFeatureEntitlementSubject
): Promise<FeatureEntitlementVerification> {
  const identity = entitlementIdentity(expected);
  const existing = preloadedEntitlements.get(cacheKey);
  if (existing?.identity === identity) {
    return existing.promise;
  }
  if (existing) {
    preloadedEntitlements.delete(cacheKey);
  }
  while (preloadedEntitlements.size >= MAX_PRELOADED_SUBJECTS) {
    evictOldestPreload();
  }

  // Verification intentionally ignores a not-yet-hydrated billing plan.
  // consumeFeatureEntitlement rechecks the current known plan before applying.
  const verificationSubject =
    expected.subjectType === 'workspace'
      ? { ...expected, plan: undefined }
      : expected;
  recordBootEvent('feature-entitlement-preload-started');
  const startedAt = performance.now();
  const promise = readAndVerifyFeatureEntitlement(
    cacheKey,
    verificationSubject
  ).finally(() => {
    recordBootEvent('feature-entitlement-preload-finished', {
      durationMs: performance.now() - startedAt,
    });
  });
  preloadedEntitlements.set(cacheKey, { identity, promise });
  return promise;
}

export async function consumeFeatureEntitlement(
  cacheKey: string,
  expected: ExpectedFeatureEntitlementSubject
): Promise<FeatureEntitlementVerification> {
  const identity = entitlementIdentity(expected);
  const existing = preloadedEntitlements.get(cacheKey);
  const promise =
    existing?.identity === identity
      ? existing.promise
      : preloadFeatureEntitlement(cacheKey, expected);
  let result: FeatureEntitlementVerification;
  try {
    result = await promise;
  } finally {
    if (preloadedEntitlements.get(cacheKey)?.promise === promise) {
      preloadedEntitlements.delete(cacheKey);
    }
  }
  if (result.status !== 'valid') {
    return result;
  }
  if (!result.expiresAt || Date.parse(result.expiresAt) <= Date.now()) {
    return emptyVerification('expired');
  }
  if (
    expected.subjectType === 'workspace' &&
    hasKnownFeaturePlanConflict(expected.plan, result.plan)
  ) {
    return emptyVerification('invalid');
  }
  return result;
}

export function invalidateFeatureEntitlementPreload(
  cacheKey: string | null
): void {
  if (cacheKey) {
    preloadedEntitlements.delete(cacheKey);
  }
}
